import { formatISTDateTime, toISTInput, fromISTInput } from "../../lib/admin-time";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useRef } from "react";
import HelmetLayout from "../../components/HelmetLayout";
import { APIContext } from "../../api/api";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";
import slugify from "slugify";
import {
  Video,
  Plus,
  Calendar,
  ExternalLink,
  Edit2,
  Trash2,
  X,
  PlayCircle,
  Clock,
} from "lucide-react";

export default function Webinars() {
  const { listOfAllWebinars, createNewWebinar, updateWebinar, deleteWebinar, getWebinar } =
    useContext(APIContext);

  const [webinarList, setWebinarList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [webinarTitle, setWebinarTitle] = useState("");
  const [webinarDescription, setWebinarDescription] = useState("");
  const [webinarSlug, setWebinarSlug] = useState("");
  const [webinarThumbnail, setWebinarThumbnail] = useState("");
  const [webinarThumbnailFile, setWebinarThumbnailFile] = useState(null);
  const [webinarYoutubeLink, setWebinarYoutubeLink] = useState("");
  const [webinarScheduledTime, setWebinarScheduledTime] = useState("");

  const thumbnailRef = useRef(null);

  useEffect(() => {
    refreshList();
  }, []);

  const refreshList = () => {
    listOfAllWebinars()
      .then((list) => {
        setWebinarList(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Could not load webinars");
        setWebinarList([]);
        setLoading(false);
      });
  };

  const openAddModal = () => {
    setEditingId(null);
    setWebinarTitle("");
    setWebinarDescription("");
    setWebinarSlug("");
    setWebinarThumbnail("");
    setWebinarThumbnailFile(null);
    setWebinarYoutubeLink("");
    setWebinarScheduledTime("");
    setModalOpen(true);
  };

  const openEditModal = (webinar) => {
    const id = webinar.pk.replace("webinar-", "");
    setEditingId(id);
    getWebinar(id)
      .then((data) => {
        setWebinarTitle(data.webinar_title || "");
        setWebinarDescription(data.webinar_description || "");
        setWebinarSlug(data.webinar_slug || "");
        setWebinarThumbnail(data.webinar_thumbnail || "");
        setWebinarYoutubeLink(data.webinar_youtube_link || "");
        setWebinarScheduledTime(
          data.webinar_scheduled_time ? String(data.webinar_scheduled_time) : ""
        );
        setWebinarThumbnailFile(null);
        setModalOpen(true);
      })
      .catch(() => {
        setWebinarTitle(webinar.webinar_title || "");
        setWebinarDescription(webinar.webinar_description || "");
        setWebinarSlug(webinar.webinar_slug || "");
        setWebinarThumbnail(webinar.webinar_thumbnail || "");
        setWebinarYoutubeLink(webinar.webinar_youtube_link || "");
        setWebinarScheduledTime(
          webinar.webinar_scheduled_time ? String(webinar.webinar_scheduled_time) : ""
        );
        setWebinarThumbnailFile(null);
        setModalOpen(true);
      });
  };

  const handleSave = () => {
    if (!webinarTitle.trim()) {
      toast.error("Please enter a webinar title");
      return;
    }
    if (isSaving) return;
    setIsSaving(true);

    const scheduled = webinarScheduledTime ? parseInt(webinarScheduledTime, 10) : undefined;

    if (!editingId) {
      createNewWebinar(
        webinarTitle,
        webinarDescription,
        webinarThumbnailFile,
        webinarYoutubeLink,
        webinarSlug,
        scheduled
      )
        .then(() => {
          toast.success("Webinar created successfully!");
          setIsSaving(false);
          setModalOpen(false);
          refreshList();
        })
        .catch((err) => {
          console.error(err);
          toast.error("Failed to create webinar.");
          setIsSaving(false);
        });
    } else {
      updateWebinar(
        editingId,
        webinarTitle,
        webinarDescription,
        webinarThumbnailFile,
        webinarYoutubeLink,
        webinarSlug,
        scheduled
      )
        .then(() => {
          toast.success("Webinar updated successfully!");
          setIsSaving(false);
          setModalOpen(false);
          refreshList();
        })
        .catch((err) => {
          console.error(err);
          toast.error("Failed to update webinar.");
          setIsSaving(false);
        });
    }
  };

  const handleDelete = () => {
    if (!editingId || isDeleting) return;
    if (!window.confirm("Are you sure you want to delete this webinar?")) return;
    setIsDeleting(true);
    deleteWebinar(editingId)
      .then(() => {
        toast.success("Webinar deleted.");
        setIsDeleting(false);
        setModalOpen(false);
        refreshList();
      })
      .catch(() => {
        toast.error("Failed to delete webinar.");
        setIsDeleting(false);
      });
  };

  return (
    <>
      <HelmetLayout title="Webinars" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Webinars</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Schedule, manage, and publish educational sessions and product demos.
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20 flex items-center gap-2"
          >
            <Plus size={16} />
            Schedule Webinar
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Webinars</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : webinarList.length}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <Video size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Scheduled / Upcoming</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading
                  ? "—"
                  : webinarList.filter((w) => {
                      if (!w.webinar_scheduled_time) return false;
                      return Number(w.webinar_scheduled_time) * 1000 > Date.now();
                    }).length}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Calendar size={22} />
            </div>
          </div>
        </div>

        {/* Webinars Grid */}
        {loading ? (
          <div className="flex flex-col justify-center items-center py-24">
            <PulseLoader color="#623ce7" size={12} />
            <p className="text-slate-400 text-sm mt-3">Loading webinars...</p>
          </div>
        ) : webinarList.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500 shadow-sm">
            <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="font-semibold text-slate-700 text-base">No webinars yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Click &quot;Schedule Webinar&quot; to create your first session.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {webinarList.map((webinar) => {
              const webinarID = webinar.pk.replace("webinar-", "");
              const isUpcoming =
                webinar.webinar_scheduled_time &&
                Number(webinar.webinar_scheduled_time) * 1000 > Date.now();

              const formattedDate = webinar.webinar_scheduled_time
                ? formatISTDateTime(webinar.webinar_scheduled_time)
                : "No date set";

              return (
                <div
                  key={webinarID}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col hover:border-[#623ce7]/40 transition-all shadow-sm group"
                >
                  <div className="h-48 relative bg-slate-100 overflow-hidden">
                    <img
                      src={webinar.webinar_thumbnail || "https://picsum.photos/seed/webinar/600/300"}
                      alt={webinar.webinar_title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = "https://picsum.photos/seed/webinar/600/300";
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                          isUpcoming
                            ? "bg-[#623ce7] text-white"
                            : "bg-slate-700 text-white"
                        }`}
                      >
                        {isUpcoming ? "Upcoming" : "Concluded"}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1 line-clamp-1">
                        {webinar.webinar_title}
                      </h3>
                      <p className="text-xs text-slate-500 mb-3 font-mono">
                        Slug: /{webinar.webinar_slug}
                      </p>
                      {webinar.webinar_description && (
                        <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                          {webinar.webinar_description}
                        </p>
                      )}

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-slate-500 text-xs">
                          <Calendar size={15} className="text-[#623ce7]" />
                          <span>{formattedDate}</span>
                        </div>
                        {webinar.webinar_youtube_link && (
                          <div className="flex items-center gap-2 text-slate-500 text-xs truncate">
                            <PlayCircle size={15} className="text-red-500 shrink-0" />
                            <a
                              href={webinar.webinar_youtube_link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline truncate"
                            >
                              {webinar.webinar_youtube_link}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex gap-2">
                      <button
                        onClick={() => openEditModal(webinar)}
                        className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-700 py-2 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1"
                      >
                        <Edit2 size={13} /> Edit Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        {modalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={() => setModalOpen(false)}
          >
            <div
              className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-lg">
                  {editingId ? "Edit Webinar" : "Schedule New Webinar"}
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={webinarTitle}
                    onChange={(e) => {
                      setWebinarTitle(e.target.value);
                      if (!editingId) {
                        setWebinarSlug(
                          slugify(e.target.value, { trim: false, strict: true, lower: true })
                        );
                      }
                    }}
                    placeholder="e.g. Mastering Grigora Website Builder"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={webinarSlug}
                    onChange={(e) =>
                      setWebinarSlug(
                        slugify(e.target.value, { trim: false, strict: true, lower: true })
                      )
                    }
                    placeholder="webinar-slug"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Scheduled Date & Time (IST)
                  </label>
                  <input
                    type="datetime-local"
                    value={
                      webinarScheduledTime
                        ? toISTInput(Number(webinarScheduledTime))
                        : ""
                    }
                    onChange={(e) => {
                      const dt = e.target.value;
                      if (!dt) {
                        setWebinarScheduledTime("");
                        return;
                      }
                      const instant = fromISTInput(dt);
                      if (instant) setWebinarScheduledTime(String(Math.floor(instant.getTime() / 1000)));
                    }}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    YouTube Stream / Video URL
                  </label>
                  <input
                    type="url"
                    value={webinarYoutubeLink}
                    onChange={(e) => setWebinarYoutubeLink(e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={webinarDescription}
                    onChange={(e) => setWebinarDescription(e.target.value)}
                    placeholder="Provide a brief overview of what this webinar covers..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3.5 py-2 text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">
                    Thumbnail
                  </label>
                  {webinarThumbnail && (
                    <img
                      src={webinarThumbnail}
                      alt="Thumbnail preview"
                      className="w-full h-36 object-cover rounded-lg border border-slate-200 mb-2"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => thumbnailRef.current?.click()}
                    className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {webinarThumbnail ? "Replace Thumbnail" : "Upload Thumbnail"}
                  </button>
                  <input
                    ref={thumbnailRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => setWebinarThumbnail(reader.result);
                      reader.readAsDataURL(file);
                      setWebinarThumbnailFile(file);
                    }}
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                {editingId ? (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                  >
                    <Trash2 size={14} /> Delete Webinar
                  </button>
                ) : (
                  <div />
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 rounded-lg text-xs font-medium bg-[#623ce7] text-white hover:bg-[#5030bc] shadow-md shadow-[#623ce7]/20 disabled:opacity-50"
                  >
                    {isSaving ? "Saving..." : editingId ? "Update Webinar" : "Create Webinar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
